import { Button, Grid, Group, Modal, Stack, TagsInput, Text, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMail, IconRotate } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useState } from 'react';
import Regex from '../../constants/Regex';
import { invoiceTypeLabel, invoiceTypeOf } from '../../helpers/invoiceDisplay';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import classes from './InvoiceSendModal.module.css';

// awesome-debounce-promise leaves every superseded call unresolved (onlyResolvesLast
// is its default), so a preview that lands late cannot overwrite a newer one.
const previewDebounced = AwesomeDebouncePromise(
  (fetch, id, subject, body) =>
    fetch(`/api/admin/invoices/${id}/email/preview`, {
      method: 'POST',
      body: JSON.stringify({ email: { subject, body } }),
    }),
  400
);

const InvoiceSendModal = ({ invoice, onClose, onSent }) => {
  const fetch = useFetch();
  const [defaults, setDefaults] = useState();
  const [email, setEmail] = useState();
  const [loadError, setLoadError] = useState();
  const [sendError, setSendError] = useState();
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState();
  const [previewError, setPreviewError] = useState();

  useEffect(() => {
    fetch(`/api/admin/invoices/${invoice.id}/email`)
      .then(({ email }) => {
        setDefaults(email);
        setEmail(email);
      })
      .catch(setLoadError);
  }, [fetch, invoice.id]);

  const { subject, body } = email ?? {};

  useEffect(() => {
    if (!email) {
      return;
    }
    setPreviewError(undefined);
    previewDebounced(fetch, invoice.id, subject, body)
      .then(({ html }) => setPreview(html))
      .catch(setPreviewError);
  }, [fetch, invoice.id, email, subject, body]);

  const recipients = email?.to ?? [];
  const invalidRecipients = recipients.filter((recipient) => !Regex.emailRegex.test(recipient));

  const send = () => {
    setSending(true);
    setSendError(undefined);
    fetch(`/api/admin/invoices/${invoice.id}/send`, {
      method: 'POST',
      body: JSON.stringify({ email: { to: recipients, subject, body } }),
    }).then(
      () => {
        notifications.show({
          title: 'Invoice sent',
          message: `Invoice sent to ${recipients.length} ${recipients.length === 1 ? 'recipient' : 'recipients'}`,
          color: 'success',
        });
        onSent();
        onClose();
      },
      (error) => {
        setSendError(error);
        setSending(false);
      }
    );
  };

  return (
    <Modal
      opened
      onClose={onClose}
      closeOnEscape={!sending}
      closeOnClickOutside={!sending}
      withCloseButton={!sending}
      size="xl"
      title={`Send ${invoiceTypeLabel(invoiceTypeOf(invoice)).toLowerCase()} # ${invoice.number}`}
    >
      {loadError && <ErrorMessage message={loadError.message} />}
      {!loadError && !email && <Spinner />}
      {email && (
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="sm">
              <TagsInput
                label="Recipients"
                placeholder="email@example.com"
                value={recipients}
                onChange={(to) => setEmail({ ...email, to })}
                error={
                  invalidRecipients.length > 0 ? `Not an email address: ${invalidRecipients.join(', ')}` : undefined
                }
              />
              <TextInput
                label="Subject"
                value={subject}
                onChange={(event) => setEmail({ ...email, subject: event.currentTarget.value })}
              />
              <Textarea
                label="Message"
                autosize
                minRows={8}
                value={body}
                onChange={(event) => setEmail({ ...email, body: event.currentTarget.value })}
              />
              <Text size="sm" c="dimmed">
                Attachment: {email.attachment?.filename}
              </Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            {previewError && <ErrorMessage message={previewError.message} />}
            {!previewError && preview === undefined && <Spinner />}
            {!previewError && preview !== undefined && (
              <iframe sandbox="" srcDoc={preview} title="Email preview" className={classes.preview} />
            )}
          </Grid.Col>
          {/* Below both columns, so the actions sit at the dialog's right edge. */}
          <Grid.Col span={12}>
            {sendError && <ErrorMessage message={sendError.message} />}
            <Group justify="flex-end" gap="xs">
              <Button
                variant="default"
                leftSection={<IconRotate size={14} />}
                disabled={sending}
                onClick={() => {
                  setEmail(defaults);
                  setSendError(undefined);
                }}
              >
                Reset to default
              </Button>
              <Button
                leftSection={<IconMail size={14} />}
                disabled={sending || recipients.length === 0 || invalidRecipients.length > 0}
                onClick={send}
              >
                Send
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
      )}
    </Modal>
  );
};

export default InvoiceSendModal;
